import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { BiCategory } from 'react-icons/bi';
import { Link, useLocation } from 'react-router-dom';

import Logo from '~/common/components/Logo.tsx';
import Container from '~/common/layouts/Container.tsx';
import Flex from '~/common/layouts/Flex.tsx';
import { breakpoint } from '~/common/utils/breakpoint.utils';
import useAuth from '~/features/auth/hooks/useAuth.ts';

const menuItems = [
  { path: '/', label: '홈' },
  { path: '/notices', label: '공지사항' },
  { path: '/products', label: '이용권 구매' },
  // TODO: 이용 가이드 — Hoya PC 콘텐츠로 업데이트 후 노출
  // { path: '/manual', label: '이용 가이드' },
  { path: '/support', label: '자주 묻는 질문' },
  { path: '/my-page', label: '마이페이지' },
];

export default function NavigationBar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { state, handleSignOut } = useAuth();

  const isLoggedIn = !!state.authUser;

  const isActiveLink = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }

    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    setOpen(false);
    window.document.body.style.overflow = 'unset';
  }, [location.pathname]);

  return (
    <nav css={[styles.navbar]}>
      <Container css={[styles.inner, open ? styles.expanded : styles.collapsed]}>
        <Flex items="center" justify="space-between">
          <div css={styles.logo}>
            <Link to="/">
              <Logo />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            css={styles.menuBtn}
            onClick={() => {
              setOpen(!open);
              window.document.body.style.overflow = !open ? 'hidden' : 'unset';
            }}
          >
            <BiCategory />
          </button>

          {/* Desktop Menu */}
          <Flex css={styles.desktopMenu} gap="32px" items="center">
            {menuItems.map(item => (
              <Link
                key={item.path}
                css={[styles.link, isActiveLink(item.path) && styles.active]}
                to={item.path}
              >
                {item.label}
              </Link>
            ))}

            {isLoggedIn ? (
              <button css={styles.startBtn} onClick={handleSignOut}>
                로그아웃
              </button>
            ) : (
              <Flex gap="10px" items="center">
                <Link to="/auth/signin">
                  <button css={styles.loginBtn}>로그인</button>
                </Link>
                <Link to="/auth/signup">
                  <button css={styles.startBtn}>회원가입</button>
                </Link>
              </Flex>
            )}
          </Flex>
        </Flex>

        {/* Expanded Mobile Menu Items */}
        <div css={[styles.mobileMenu, open && styles.mobileMenuOpen]}>
          {menuItems.map(item => (
            <Link key={item.path} css={styles.menuItem} to={item.path}>
              {item.label}
            </Link>
          ))}

          {isLoggedIn ? (
            <button css={styles.ctaMobile} onClick={handleSignOut}>
              로그아웃
            </button>
          ) : (
            <>
              <Link to="/auth/signup">
                <button css={styles.ctaMobile}>회원가입</button>
              </Link>
              <Link to="/auth/signin">
                <button css={styles.loginMobile}>로그인</button>
              </Link>
            </>
          )}
        </div>
      </Container>
    </nav>
  );
}

const styles = {
  navbar: css`
    position: fixed;
    top: 24px;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: calc(100% - 24px);
    max-width: 1200px;

    overflow: hidden;
    z-index: 1000;
  `,

  inner: css`
    pointer-events: auto;
    padding: 10px 22px;
    border-radius: 24px;

    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.08) 40%,
      rgba(255, 255, 255, 0.04) 100%
    );
    backdrop-filter: blur(14px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.09);

    transition:
      height 0.35s cubic-bezier(0.2, 0.8, 0.2, 1),
      border-radius 0.35s ease,
      background 0.3s ease;
  `,

  collapsed: css`
    height: 52px;
    border-radius: 24px;

    ${breakpoint.lg} {
      height: auto;
    }
  `,

  expanded: css`
    height: calc(100vh - 48px);
    height: calc(100dvh - 48px);
    border-radius: 24px;
    background: rgba(18, 18, 32, 0.65);
  `,

  logo: css`
    color: #fff;
    font-weight: 700;
    font-size: 20px;

    && img {
      position: absolute;
      top: -4px;
      left: 20px;
      height: 60px;
    }

    ${breakpoint.lg} {
      padding: 0 8px;
    }
  `,

  desktopMenu: css`
    display: none !important;

    ${breakpoint.lg} {
      display: flex !important;
    }
  `,

  link: css`
    color: rgba(255, 255, 255, 0.8);
    transition: 0.25s;

    &:hover {
      color: #fff;
    }
  `,

  active: css`
    color: #fff;
    font-weight: bold;
  `,

  menuBtn: css`
    display: block;
    font-size: 24px;
    background: none;
    border: none;
    color: #fff;

    ${breakpoint.lg} {
      display: none;
    }
  `,

  mobileMenu: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 28px;
    opacity: 0;
    transform: translateY(20px);
    pointer-events: none;

    transition: all 0.35s ease;

    ${breakpoint.lg} {
      display: none;
    }
  `,

  mobileMenuOpen: css`
    opacity: 1;
    transform: translateY(40px);
    pointer-events: auto;
  `,

  menuItem: css`
    font-size: 24px;
    color: #fff;
    opacity: 0.9;
  `,

  ctaMobile: css`
    margin-top: 32px;
    padding: 14px 28px;
    border-radius: 12px;
    border: none;
    background: linear-gradient(90deg, #00b4f0, #1466e8);
    color: #fff;
    font-size: 18px;
    font-weight: 600;
  `,

  loginBtn: css`
    padding: 8px 18px;
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.3);
    font-weight: 600;
    cursor: pointer;
    transition: 0.2s;

    &:hover {
      border-color: rgba(255, 255, 255, 0.6);
      color: #fff;
    }
  `,

  startBtn: css`
    padding: 8px 18px;
    border-radius: 8px;
    background: linear-gradient(90deg, #00b4f0, #1466e8);
    color: #fff;
    border: none;
    font-weight: 600;
    cursor: pointer;
  `,

  loginMobile: css`
    padding: 14px 28px;
    border-radius: 12px;
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.3);
    font-size: 18px;
    font-weight: 600;
  `,
};
